<template>
	<div class="zoom-img">
		<div class="zoom-img__container">
			<button class="zoom-img__toggle" :aria-label="ariaZoomIn" @click="toggle" :data-src="src">
				<svg
					width="28"
					height="28"
					viewBox="0 0 28 28"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M16.7588 11.2214L26.5505 1.42969M26.5505 1.42969V9.26302M26.5505 1.42969H18.7171"
						stroke="black"
						stroke-width="2.33333"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
					<path
						d="M11.1999 16.7812L1.4082 26.5729M1.4082 26.5729V18.7396M1.4082 26.5729H9.24154"
						stroke="black"
						stroke-width="2.33333"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</svg>
			</button>
			<PictureImg v-if="image" class="zoom-img__image" :media="image" />
		</div>

		<div class="zoom-img__modal" v-show="isOpen">
			<div class="zoom-img__modal__backdrop" @click="toggle"></div>
			<div class="zoom-img__modal__container">
				<button class="zoom-img__toggle" :aria-label="ariaZoomOut" @click="toggle">
					<svg
						width="18"
						height="18"
						viewBox="0 0 18 18"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M15.2713 17.4136C15.8571 17.9994 16.8068 17.9994 17.3926 17.4136C17.9784 16.8278 17.9784 15.8781 17.3926 15.2923L11.101 9.00068L17.3926 2.7091C17.9784 2.12331 17.9784 1.17356 17.3926 0.587777C16.8068 0.00199068 15.8571 0.00199123 15.2713 0.587777L8.97969 6.87936L2.6881 0.587777C2.10232 0.00199123 1.15257 0.00199068 0.566781 0.587777C-0.0190054 1.17356 -0.0190049 2.12331 0.566781 2.7091L6.85837 9.00068L0.566783 15.2923C-0.0190033 15.8781 -0.0190033 16.8278 0.566783 17.4136C1.15257 17.9994 2.10232 17.9994 2.6881 17.4136L8.97969 11.122L15.2713 17.4136Z"
							fill="black"
						/>
					</svg>
				</button>

				<ClientOnly>
					<zoompinch
						class="zoom-img__modal__canvas"
						ref="zoompinchRef"
						:width="imageHighRes.width"
						:height="imageHighRes.height"
						:offset="{ top: 10, right: 10, bottom: 10, left: 10 }"
						v-model:transform="transform"
						:min-scale="0.1"
						:max-scale="10"
						:rotation="false"
						:bounds="false"
						:mouse="true"
						:touch="true"
						:wheel="true"
						:gesture="true"
					>
						<template #canvas>
							<img
								v-if="src"
								:src="src"
								:alt="imageHighRes.alt"
								:width="imageHighRes.width"
								:height="imageHighRes.height"
								class="zoom-img__modal__image"
							/>
						</template>
					</zoompinch>
				</ClientOnly>
			</div>
		</div>
	</div>
</template>

<script setup>
import { Zoompinch } from "zoompinch";
import "zoompinch/style.css";

const img = useImage();
const { replaceImageSrc } = useImageHelpers();

const zoompinchRef = useTemplateRef("zoompinchRef");
const transform = ref({ x: 0, y: 0, scale: 1, rotate: 0 });

const props = defineProps({
	fields: { type: Object, required: true },
	rendering: { type: Object, required: false },
	image: { type: Object, default: null, required: false },
	imageHighRes: { type: Object, default: null, required: false },
});

const image = computed(() =>
	props.image ? props.image : props.fields?.image?.value
);
const imageHighRes = computed(() => {
	if (props.imageHighRes) {
		return props.imageHighRes;
	} else if (props.fields?.imageHighRes?.value?.src) {
		return props.fields.imageHighRes.value;
	} else {
		return image.value;
	}
});

const src = computed(() => {
	if (imageHighRes.value?.src) {
		return img(replaceImageSrc(imageHighRes.value.src), { quality: 90 });
	}
	return null;
});

const ariaZoomIn = computed(() => props.fields?.ariaZoomIn?.value || "Zoom In");
const ariaZoomOut = computed(() => props.fields?.ariaZoomOut?.value || "Zoom Out");

onMounted(() => {
	setTimeout(() => fit());
});

function fit() {
	transform.value = { x: 0, y: 0, scale: 1, rotate: 0 };
}

watch(
	() => zoompinchRef.value?.wrapperBounds,
	() => {
		requestAnimationFrame(() => {
			zoompinchRef.value?.applyTransform(1, [0.5, 0.5], [0.5, 0.5], false);
		});
	}
);

const isOpen = ref(false);
function toggle() {
	isOpen.value = !isOpen.value;
}
</script>

<style lang="scss">
.zoom-img {
	padding: var(--spacing-2, 16px);

	.zoom-img__container {
		position: relative;
	}

	.zoom-img__image {
		overflow: hidden;
		width: 100%;
		height: auto;

		img {
			margin: 0;
			padding: 0;
			width: 100%;
			height: auto;
			border-radius: 18px;
		}
	}

	.zoom-img__toggle {
		background: var(--color-white, #fff);
		border-radius: 50%;
		border: 0;
		position: absolute;
		right: var(--spacing-2, 16px);
		top: var(--spacing-2, 16px);
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;

		&:hover {
			cursor: pointer;
			opacity: 0.8;
		}
	}

	.zoom-img__toggle svg {
		width: 20px;
		height: 20px;
	}

	.zoom-img__modal {
		position: fixed;
		width: 100%;
		height: 100vh;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 999;
	}

	.zoom-img__modal__backdrop {
		background: rgba(0, 0, 0, 0.8);
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: 1;
	}

	.zoom-img__modal__image {
		position: relative;
		z-index: 2;
		border-radius: 18px;
	}

	.zoom-img__modal__container {
		position: relative;
		width: 100%;
		height: 90%;
	}

	.zoom-img__modal__container .zoom-img__toggle {
		z-index: 10;

		svg {
			width: 15px;
			height: 15px;
		}
	}

	.zoom-img__modal__canvas {
		z-index: 9;
	}
}
</style>
